/**
 * Context for docs markdown rendering.
 *
 * Provides the current doc slug and available slugs to block components
 * (like DocsRichTextBlock) so they can render headings with ids that match
 * the manifest and resolve internal links correctly.
 */
import { createContext, useContext, useMemo, type ReactNode } from 'react';

export interface DocsMarkdownContextValue {
  /** Current doc slug (e.g. 'quickstart', 'guides/auth'). */
  currentSlug: string;
  /** Set of all available doc slugs for link validation. */
  availableSlugs: Set<string>;
  /** Base path for docs routes. Defaults to '/docs'. */
  docsBasePath: string;
}

const DocsMarkdownContext = createContext<DocsMarkdownContextValue | null>(null);

export interface DocsMarkdownProviderProps {
  currentSlug: string;
  availableSlugs: string[] | Set<string>;
  docsBasePath?: string;
  children: ReactNode;
}

export function DocsMarkdownProvider({
  currentSlug,
  availableSlugs,
  docsBasePath = '/docs',
  children,
}: DocsMarkdownProviderProps) {
  const slugSet = useMemo(
    () => (availableSlugs instanceof Set ? availableSlugs : new Set(availableSlugs)),
    [availableSlugs],
  );

  const value = useMemo<DocsMarkdownContextValue>(
    () => ({ currentSlug, availableSlugs: slugSet, docsBasePath }),
    [currentSlug, slugSet, docsBasePath],
  );

  return <DocsMarkdownContext.Provider value={value}>{children}</DocsMarkdownContext.Provider>;
}

/**
 * Hook to access the docs markdown context.
 *
 * Returns null if used outside a DocsMarkdownProvider — callers should
 * handle this gracefully (e.g. fall back to simpler rendering without
 * heading ids or link resolution).
 */
export function useDocsMarkdownContext(): DocsMarkdownContextValue | null {
  return useContext(DocsMarkdownContext);
}
